import { useMemo, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Panel } from "@/components/ui/panel";
import { SkillsPicker } from "@/components/profile/skills-picker";
import { useMySkillIds, useSkillWriters } from "@/hooks/use-skills";
import type { TSkill } from "@/types/skill";

interface MySkillsPickerProps {
  workspaceSlug: string;
  skills: TSkill[];
}

/**
 * The signed-in member's own skills: the cached "my skills" read (NG-2802),
 * saved through the skill writers, which put the saved set into that read.
 */
export const MySkillsPicker = observer(function MySkillsPicker({
  workspaceSlug,
  skills,
}: MySkillsPickerProps) {
  const { t } = useTranslation();
  const mine = useMySkillIds(workspaceSlug);
  const writers = useSkillWriters(workspaceSlug);
  const [isSaving, setIsSaving] = useState(false);
  const selectedIds = useMemo(() => new Set(mine.data), [mine.data]);

  if (skills.length === 0) {
    return (
      <Panel>
        <p className="text-sm text-muted-foreground">{t("profile.skills.empty")}</p>
      </Panel>
    );
  }

  const handleChange = async (nextIds: string[]) => {
    setIsSaving(true);
    try {
      await writers.setMySkills(nextIds);
      toast.success(t("profile.skills.save_success"));
    } catch (error) {
      console.error("[profile-skills] save failed", { workspaceSlug, skillIds: nextIds, error });
      toast.error(t("profile.skills.save_error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Panel>
      <SkillsPicker
        skills={skills}
        selectedIds={selectedIds}
        isSaving={isSaving}
        onChange={handleChange}
      />
    </Panel>
  );
});
